import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import UnsavedChangesGuard from './UnsavedChangesGuard'
import { toast } from 'sonner'
import { Bug, Pencil, Check, X, Filter } from 'lucide-react'
import BoardLayout from '@/components/layout/BoardLayout'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { isNodeApproved } from '@/service/graph'
import EditorWrapper from './EditorWrapper'
import { Button } from '@/components/ui/button'
import VersionSelector from './VersionSelector'
import { useTranslation } from '@/i18n'
import { useBugReport } from '@/components/bug-report/useBugReport'
import { apiService } from '@/service/api'
import { IO_LINE_TRACING_ENABLED } from '@/config/ioLineTracing'
import type { DocumentRecord } from '@/types/catalog'
import type { SheetConnector, SheetConnectorEdge } from '@/types/sheet-connector'
import { useSheetConnectors } from './useSheetConnectors'
import { buildConnectorNavUrl } from './connectorNav'
import { useConnectedGraphs } from './useConnectedGraphs'
import { hasConnectedPnids } from '@/types/connected-graphs'
import ConnectedPnidsPanelV2 from './ConnectedPnidsPanelV2'
import SheetConnectorModal from './SheetConnectorModal'
import SheetPageBar from './SheetPageBar'
import { pnidPageEntriesFrom, type PnidPageEntry } from '@/utils/resolveGraphOpen'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import SldViewSwitch from './SldViewSwitch'
import { sldGraphViewsFor, useSldGraphViews, type SldGraphView } from './sldGraphViews'
import { useUIv2 } from '@/hooks/useUIv2'
import AppSidebar from '@/components/AppSidebar'
import GraphEditorNavbarV2 from './GraphEditorNavbarV2'
import GraphEditorSubNavbarV2 from './GraphEditorSubNavbarV2'
import PendingReviewPanelV2 from './PendingReviewPanelV2'
import LabelingFilterPanelV2 from './LabelingFilterPanelV2'
import { TaxonomyProvider } from './TaxonomyContext'
import type { DrawingDiscipline } from '@/config/classification'
import TableGridPanelV2 from './TableGridPanelV2'
import type { TableRegion } from './tableGrid'
import VersionHistoryPanelV2 from './VersionHistoryPanelV2'

export const EDITOR_MODE = 'editor mode'
export const GRAPH_PREVIEW = 'graph preview'
export const JSON_VIEW = 'json view'
export const ORIGINAL_VIEW = 'original view'
export const LINES_VIEW = 'lines'

interface TabsProps {
  tabKeys: string[]
  activeTab: string
  hasBoundingBoxes: boolean
  onTabChange: (tabKey: string) => void
  getTabLabel: (tabKey: string) => string
  noBboxText: string
}

const EditorTabs = ({
  tabKeys,
  activeTab,
  hasBoundingBoxes,
  onTabChange,
  getTabLabel,
  noBboxText,
}: TabsProps) => (
  <div className="flex bg-gray-100 dark:bg-dark-accent py-2 px-4 rounded-lg gap-3">
    {tabKeys.map((tabKey) => {
      const isActive = activeTab === tabKey
      if (!hasBoundingBoxes && tabKey === EDITOR_MODE) {
        return null
      }
      return (
        <Button
          className="capitalize"
          key={tabKey}
          data-testid={tabKey}
          size="sm"
          variant={isActive ? 'tabActive' : 'tabInactive'}
          onClick={() => onTabChange(tabKey)}
          disabled={!hasBoundingBoxes && tabKey === EDITOR_MODE}
        >
          {getTabLabel(tabKey)}
        </Button>
      )
    })}
    {!hasBoundingBoxes && (
      <div className="text-sm text-gray-500 dark:text-dark-text-muted flex items-center">{noBboxText}</div>
    )}
  </div>
)

interface DrawingNumberEditorProps {
  currentDrawingNumber: string
  editingDrawingNumber: boolean
  drawingNumberEdit: string
  savingDrawingNumber: boolean
  onEditChange: (value: string) => void
  onSave: () => void
  onCancel: () => void
  onStartEdit: () => void
  t: ReturnType<typeof useTranslation>['t']
}

const DrawingNumberEditor = ({
  currentDrawingNumber,
  editingDrawingNumber,
  drawingNumberEdit,
  savingDrawingNumber,
  onEditChange,
  onSave,
  onCancel,
  onStartEdit,
  t,
}: DrawingNumberEditorProps) => (
  <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-card border rounded-lg shadow-sm">
    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
      {t('data_catalog:graph_editor.drawing_number_label')}:
    </span>
    {editingDrawingNumber ? (
      <>
        <input
          type="text"
          value={drawingNumberEdit}
          onChange={e => onEditChange(e.target.value)}
          placeholder={t('data_catalog:graph_editor.drawing_number_placeholder')}
          className="flex-1 min-w-0 h-8 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md"
          autoFocus
        />
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          disabled={savingDrawingNumber}
          onClick={onSave}
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8"
          disabled={savingDrawingNumber}
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
        </Button>
      </>
    ) : (
      <>
        <span className="text-sm text-gray-700 dark:text-gray-300 min-w-0 truncate">
          {currentDrawingNumber || '—'}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 shrink-0"
          onClick={onStartEdit}
          title={t('data_catalog:graph_editor.drawing_number_edit_hint')}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </>
    )}
  </div>
)

/**
 * The editor route, wrapped in its taxonomy provider by {@link GraphEditor}.
 *
 * Kept as an inner component ON PURPOSE: this route has three exits (embed, V2,
 * legacy), and wrapping each one individually meant the V2 branch — the live
 * one — returned before ever reaching a provider, leaving the labeling filter
 * this route renders on the P&ID taxonomy for an SLD. One boundary cannot be
 * missed by a fourth exit added later.
 */
function GraphEditorRoute({
  onDisciplineResolved,
}: {
  onDisciplineResolved: (discipline: DrawingDiscipline) => void
}) {
  const { t } = useTranslation()
  const isV2 = useUIv2()
  const { openBugReport } = useBugReport()
  const { graph_id } = useParams<{ graph_id: string }>()

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // ?embed=1 hides the AppSidebar + navbars so the same graph viewer can
  // render inside an iframe overlay (e.g. KG preview floating dialog).
  const isEmbedded = searchParams.get('embed') === '1'
  const { fetchComments, currentState, publishGraph, user } =
    useAppStore()
  // Annotators are read-only: no preview/export tabs, no download button.
  const isAnnotator = isAnnotatorRole(user?.role)

  // Count pending review items — nodes an annotator hasn't approved yet (ENG-2622:
  // approval is the explicit `approved` flag, not the model's confidence).
  const pendingReviewCount = useMemo(() => {
    let count = 0
    for (const node of currentState.nodes.values()) {
      if ((node.type === 'symbol' || node.type === 'tag') && !isNodeApproved(node)) count++
    }
    return count
  }, [currentState.nodes])

  // Get documentId from query params (for P&ID pages from document viewer)
  const documentId = searchParams.get('documentId')

  // An SLD page has an annotation graph and, once parsed, a parse graph beside
  // it (VISN-304). Only with the parse flag on: off, the editor is unchanged.
  const { enabled: sldParsingEnabled } = useApiFeatureFlag('sld_parsing_enabled', false)
  const sldViews = useSldGraphViews(documentId, graph_id, sldParsingEnabled)
  // The parse graph is read-only: the editor opens it read-only, and the server
  // refuses saves to it.
  const isParsedSldView = sldViews?.view === 'parsed'
  const openSldView = useCallback(
    async (view: SldGraphView) => {
      // Resolved fresh: a re-parse replaces the parsed graph, so the id the
      // switch last saw may already be gone.
      let views = sldViews
      if (documentId && graph_id) {
        const doc = await apiService.getDocument(documentId).catch(() => null)
        views = (doc && sldGraphViewsFor(doc, graph_id)) || sldViews
      }
      const target = view === 'parsed' ? views?.parsedGraphId : views?.annotationGraphId
      if (target) navigate(`/graph-editor/${target}?${searchParams.toString()}`, { replace: true })
    },
    [documentId, graph_id, sldViews, navigate, searchParams],
  )
  // Get multi-page set info from query params
  const multiPageSetId = searchParams.get('multiPageSet')
  const docIdsParam = searchParams.get('docIds')
  // Per-page graph_ids for single multi-page PDF (format: "1:uuid1,2:uuid2,3:uuid3")
  const pnidPagesParam = searchParams.get('pnidPages')
  // boardName comes from the ?boardName= query param, which is set ONLY when
  // navigating from a board (see DocumentPreview.getEditLink). When a document is
  // opened from the Data Catalog there is no param, so boardName is null and the
  // breadcrumb correctly shows "Data Catalog". (Previously this fell back to the
  // active board in the store, which wrongly showed the current/Master board for
  // catalog-opened documents.)
  const boardName = searchParams.get('boardName') || null
  const docNameParam = searchParams.get('docName')

  const navigateBack = useNavigateBack()
  const [activeTab, setActiveTab] = useState<string>(EDITOR_MODE)
  // Start with true to keep default Editor Mode until we know if graph has bounding boxes
  const [hasBoundingBoxes, setHasBoundingBoxes] = useState(true)
  // Multi-page set state
  const [multiPageDocs, setMultiPageDocs] = useState<DocumentRecord[]>([])
  const [currentSheetIndex, setCurrentSheetIndex] = useState(0)
  const [loadingMultiPage, setLoadingMultiPage] = useState(false)
  // Sheet connectors
  const { connectors: sheetConnectors, create: createSheetConnector, createWithMirror: createSheetConnectorWithMirror, update: updateSheetConnector, remove: removeSheetConnector } = useSheetConnectors(documentId || undefined)
  const [connectorModalOpen, setConnectorModalOpen] = useState(false)
  const [connectorModalEdge, setConnectorModalEdge] = useState<SheetConnectorEdge>('right')
  const [editingConnector, setEditingConnector] = useState<SheetConnector | null>(null)
  const [contextMenuConnector, setContextMenuConnector] = useState<{ connector: SheetConnector; x: number; y: number } | null>(null)

  // Close context menu on Escape key
  useEffect(() => {
    if (!contextMenuConnector) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenuConnector(null)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [contextMenuConnector])

  const [editingDrawingNumber, setEditingDrawingNumber] = useState(false)
  type RightPanel = 'review' | 'history' | 'addObject' | 'connected' | 'labelFilter' | 'tables' | null
  const [activeRightPanel, setActiveRightPanel] = useState<RightPanel>(null)
  // The parse view has no panels to show: they work on the editable graph.
  useEffect(() => {
    if (isParsedSldView) setActiveRightPanel(null)
  }, [isParsedSldView])
  const showReviewPanel = activeRightPanel === 'review'
  const showHistoryPanel = activeRightPanel === 'history'
  const showConnectedPanel = activeRightPanel === 'connected'
  const showFilterPanel = activeRightPanel === 'labelFilter'
  const showTablesPanel = activeRightPanel === 'tables'
  // The canvas multi-selection of table cells, for the Tables panel's merge/unmerge actions.
  const [selectedCellIds, setSelectedCellIds] = useState<string[]>([])
  const addObjectMode = activeRightPanel === 'addObject'
  // Which table the grid panel shows, and which cell has focus — the canvas draws only that
  // table and spotlights that cell, so reading a column stays anchored to the drawing.
  const [activeTableIndex, setActiveTableIndex] = useState<number | null>(null)
  const [focusedTableCellId, setFocusedTableCellId] = useState<string | null>(null)
  // A cell clicked on the DRAWING, pushed back into the grid so it scrolls + focuses.
  const [canvasPickedCellId, setCanvasPickedCellId] = useState<string | null>(null)
  // Set while the annotator draws the region for a brand-new (unparsed) table, then
  // holds the box they drew until the panel turns it into a blank grid.
  const [drawingTableRegion, setDrawingTableRegion] = useState(false)
  const [pendingTableRegion, setPendingTableRegion] = useState<TableRegion | null>(null)

  /**
   * Switch the right-hand panel, abandoning any armed table-region draw.
   *
   * Every panel button goes through this. Leaving the draw armed while the Tables panel is
   * hidden makes the canvas swallow the next drawn rectangle as a table region instead of
   * creating the object the annotator asked for, and strands a pending region that reappears
   * when Tables is reopened.
   */
  const switchRightPanel = useCallback((next: RightPanel) => {
    setDrawingTableRegion(false)
    setPendingTableRegion(null)
    setActiveRightPanel(next)
  }, [])
  // Fetch connected P&IDs eagerly so we know whether to surface the navbar
  // entry point at all — the button (and panel) stay hidden when there's
  // nothing connected, rather than opening to an empty "No connected P&IDs".
  const { data: connectedGraphs } = useConnectedGraphs(graph_id, !!graph_id)
  const showConnected = hasConnectedPnids(connectedGraphs)
  // Objects spotlighted + framed on the drawing by the Pending Review panel (FS-523).
  const [reviewHighlightIds, setReviewHighlightIds] = useState<Set<string>>(new Set())
  // FS-623: the labeling filter's matches get their OWN highlight state — separate
  // from Pending Review's — so the two always-mounted panels can't stomp each
  // other's spotlight via a shared setter on a direct panel-to-panel switch.
  const [filterHighlightIds, setFilterHighlightIds] = useState<Set<string>>(new Set())
  // FS-623 focus mode: when on, the filter dims every non-matched shape so matches
  // stand alone. Owned here so it survives the panel and reaches the renderer.
  const [filterFocusMode, setFilterFocusMode] = useState(false)
  // FS-623 follow-up: publishGraph persists + re-hydrates the whole graph, which can
  // take a few seconds. Track it so the navbar shows "Saving…" and the cursor turns
  // to a wait/progress state (Armin) instead of the save looking hung.
  const [isSaving, setIsSaving] = useState(false)
  const [resolvedDocName, setResolvedDocName] = useState('')
  // V2 inline search state (lifted from sub-navbar → wired to EditorWrapper)
  const [v2SearchTerm, setV2SearchTerm] = useState('')
  const [v2SearchMatchInfo, setV2SearchMatchInfo] = useState<{ total: number; current: number }>({ total: 0, current: 0 })
  // JSON tab view mode: live annotation graph ('graph') vs GT export ('gt').
  const [jsonMode, setJsonMode] = useState<'graph' | 'gt'>('graph')
  const v2SearchNextRef = useRef<(() => void) | null>(null)
  const v2SearchPreviousRef = useRef<(() => void) | null>(null)
  const [drawingNumberEdit, setDrawingNumberEdit] = useState('')
  const [savingDrawingNumber, setSavingDrawingNumber] = useState(false)

  // Only show JSON tab for internal organization users (for debugging/testing).
  // LINES_VIEW is gated behind the IO_LINE_TRACING env flag.
  const tabKeys = useMemo(() => {
    const baseTabs = IO_LINE_TRACING_ENABLED
      ? [EDITOR_MODE, GRAPH_PREVIEW, LINES_VIEW, ORIGINAL_VIEW]
      : [EDITOR_MODE, GRAPH_PREVIEW, ORIGINAL_VIEW]
    // Include JSON tab only for internal org users — inserted right before ORIGINAL_VIEW.
    if (user?.isInternalOrg) {
      const originalIndex = baseTabs.indexOf(ORIGINAL_VIEW)
      baseTabs.splice(originalIndex, 0, JSON_VIEW)
    }
    // Annotators keep Graph Preview but not the raw JSON tab.
    if (isAnnotator) {
      return baseTabs.filter((t) => t !== JSON_VIEW)
    }
    return baseTabs
  }, [user?.isInternalOrg, isAnnotator])

  // A tab can disappear under the user (its gate changes after it was picked), and the body
  // would carry on rendering a tab the header no longer shows. Fall back to the editor when
  // the active tab is removed.
  useEffect(() => {
    if (!tabKeys.includes(activeTab)) setActiveTab(EDITOR_MODE)
  }, [tabKeys, activeTab])

  const getTabLabel = useCallback(
    (tabKey: string) => {
      switch (tabKey) {
        case EDITOR_MODE:
          return t('data_catalog:graph_editor.tabs.editor')
        case GRAPH_PREVIEW:
          return t('data_catalog:graph_editor.tabs.preview')
        case JSON_VIEW:
          return 'JSON'
        case LINES_VIEW:
          return 'Lines'
        case ORIGINAL_VIEW:
          return t('data_catalog:graph_editor.tabs.original')
        default:
          return tabKey
      }
    },
    [t]
  )

  const handleTabChange = useCallback((tabKey: string) => {
    setActiveTab(tabKey)
  }, [])

  useEffect(() => {
    if (!graph_id) return
    fetchComments(graph_id)
  }, [graph_id, fetchComments])

  // Load multi-page set documents if applicable
  useEffect(() => {
    if (!multiPageSetId || !docIdsParam) return

    const loadMultiPageSet = async () => {
      setLoadingMultiPage(true)
      try {
        const docIds = docIdsParam.split(',').filter(Boolean)
        const docs = await Promise.all(
          docIds.map(id => apiService.getDocument(id))
        )
        // Sort by sheet_index
        const sorted = docs.sort((a, b) => {
          const aIndex = a.properties.sheet_index ? parseInt(a.properties.sheet_index, 10) : 0
          const bIndex = b.properties.sheet_index ? parseInt(b.properties.sheet_index, 10) : 0
          return aIndex - bIndex
        })
        setMultiPageDocs(sorted)
        
        // Find current document index
        if (documentId) {
          const currentIndex = sorted.findIndex(d => d.id === documentId)
          if (currentIndex >= 0) {
            setCurrentSheetIndex(currentIndex)
          }
        }
      } catch (error) {
        console.error('Failed to load multi-page set:', error)
      } finally {
        setLoadingMultiPage(false)
      }
    }

    loadMultiPageSet()
  }, [multiPageSetId, docIdsParam, documentId])

  // The sheet list for a single multi-page PDF, from the document itself.
  // Only the one-file-many-pages case: a grouped SET is a different shape and
  // still travels as multiPageSet + docIds.
  const [fetchedPageEntries, setFetchedPageEntries] = useState<PnidPageEntry[] | null>(null)

  useEffect(() => {
    // Clear first. This route navigates IN PLACE between documents, so without
    // the reset the previous document's sheet list stays live — either for the
    // duration of the fetch, or permanently if the new URL has no documentId at
    // all. The bar would then page the user into another document's graphs.
    setFetchedPageEntries(null)
    // The param, when present, already carries the map — don't re-fetch it.
    if (pnidPagesParam || multiPageSetId || !documentId) return
    let cancelled = false
    apiService
      .getDocument(documentId)
      .then(doc => {
        if (cancelled) return
        const entries = pnidPageEntriesFrom(doc?.properties)
        setFetchedPageEntries(entries.length > 1 ? entries : null)
      })
      .catch(() => {
        // A single-sheet document, or one still processing. Not an error —
        // there is simply no page bar to show.
        if (!cancelled) setFetchedPageEntries(null)
      })
    return () => { cancelled = true }
  }, [documentId, pnidPagesParam, multiPageSetId])

  // Parse the pnidPages param when a caller supplies it. Kept for links minted
  // before the editor looked the map up itself, and for callers that hold the
  // map but not a document id.
  const pnidPageEntries = useMemo(() => {
    if (!pnidPagesParam) return fetchedPageEntries
    // Format: "1:uuid1,2:uuid2,3:uuid3"
    return pnidPagesParam.split(',')
      .map(entry => {
        const colonIdx = entry.indexOf(':')
        if (colonIdx === -1) return null
        const page = Number(entry.slice(0, colonIdx))
        const graphId = entry.slice(colonIdx + 1)
        if (isNaN(page) || !graphId) return null
        return { page, graphId }
      })
      .filter((e): e is { page: number; graphId: string } => e !== null)
      .sort((a, b) => a.page - b.page)
  }, [pnidPagesParam, fetchedPageEntries])

  // Memoised, and declared UP HERE rather than beside its use below: the
  // `if (!graph_id) return` guard further down means a hook after it would be
  // conditional. GraphEditor re-renders on every keystroke/panel toggle, and a
  // fresh array each time defeats memoisation in the children it feeds
  // (EditorWrapper → SheetPageBar, and SheetEdgeNav).
  const sheetNames = useMemo(() => multiPageDocs.map(d => d.name), [multiPageDocs])

  // Build virtual multiPageDocs from pnidPages for single multi-page PDFs
  useEffect(() => {
    if (multiPageSetId) return // Don't override real multi-page set
    if (!pnidPageEntries || pnidPageEntries.length === 0) {
      // Drop the previous document's sheets. Clearing the entries alone is not
      // enough — the derived docs live in their own state, so on an in-place
      // navigation to a single-sheet document they would otherwise survive and
      // keep its sheet bar pointing at the old document's graphs.
      setMultiPageDocs([])
      setCurrentSheetIndex(0)
      return
    }

    // Create synthetic DocumentRecord-like objects for sheet navigation
    const virtualDocs = pnidPageEntries.map((entry, index) => ({
      id: entry.graphId,
      name: `Page ${entry.page}`,
      properties: {
        graph_id: entry.graphId,
        sheet_index: String(index),
      },
    })) as DocumentRecord[]

    setMultiPageDocs(virtualDocs)

    // Set current sheet index based on current graph_id
    const currentIdx = pnidPageEntries.findIndex(e => e.graphId === graph_id)
    if (currentIdx >= 0) {
      setCurrentSheetIndex(currentIdx)
    }
  }, [pnidPageEntries, graph_id, documentId, multiPageSetId])

  // Navigate to a specific sheet
  const navigateToSheet = useCallback((index: number) => {
    // Already on it. Beyond saving a pointless route change, this protects the
    // grouped-set guard below: re-navigating to the same document leaves the
    // loader effect's deps untouched, so nothing would ever clear the flag and
    // the page bar would stay disabled for good.
    if (index === currentSheetIndex) return

    // Handle single multi-page PDF navigation
    if (pnidPageEntries && pnidPageEntries.length > 0 && !multiPageSetId) {
      const entry = pnidPageEntries[index]
      if (!entry) return
      // documentId alone carries the sheet list — re-serialising the whole map
      // here rebuilt the oversized URL on every single sheet change. Only pass
      // pnidPages back when this navigation arrived with it.
      const params = new URLSearchParams({ editor: 'd3' })
      if (documentId) params.set('documentId', documentId)
      if (pnidPagesParam) params.set('pnidPages', pnidPagesParam)
      navigate(`/graph-editor/${entry.graphId}?${params.toString()}`, { replace: true })
      setCurrentSheetIndex(index)
      return
    }

    if (!multiPageDocs[index]) return

    const doc = multiPageDocs[index]

    // Check if graph exists for this document
    if (!doc.properties.graph_id) {
      toast.info(
        t('data_catalog:graph_not_ready', 'Graph for this sheet is still being processed. Please wait a moment.')
      )
      return
    }

    const newDocIds = multiPageDocs.map(d => d.id).join(',')
    // Arm the in-flight guard HERE, not only in the loader effect. That effect
    // runs after the route change lands, so between this click and that tick
    // the page controls are still live and a second click queues another
    // navigation against the doc list this one is about to replace. The
    // effect's `finally` is what clears it.
    setLoadingMultiPage(true)
    navigate(`/graph-editor/${doc.properties.graph_id}?editor=d3&multiPageSet=${multiPageSetId}&docIds=${newDocIds}&documentId=${doc.id}`, { replace: true })
    setCurrentSheetIndex(index)
  }, [pnidPageEntries, multiPageSetId, multiPageDocs, documentId, pnidPagesParam, navigate, t, currentSheetIndex])

  /**
   * Try to resolve a connector label to a sheet index.
   * Returns the index or -1 if not found.
   *
   * Matching order:
   * 1. Exact drawing_number match (case-insensitive)
   * 2. drawing_number appears as a substring inside the label
   * 3. Label appears as a substring inside a drawing_number
   * 4. Label interpreted as a 1-based sheet number
   */
  const resolveConnectorLabel = useCallback((label: string): number => {
    const trimmed = label.trim().toLowerCase()
    if (!trimmed || !multiPageSetId || multiPageDocs.length === 0) return -1

    // 1. Exact drawing_number match
    const exactIdx = multiPageDocs.findIndex(
      d => (d.properties.drawing_number ?? '').trim().toLowerCase() === trimmed
    )
    if (exactIdx >= 0) return exactIdx

    // 2. A document's drawing_number appears inside the label (e.g. label contains "RA-279032.024")
    // Require minimum 4 chars to avoid false positives on short drawing numbers (e.g. "024")
    const substringIdx = multiPageDocs.findIndex(d => {
      const dn = (d.properties.drawing_number ?? '').trim().toLowerCase()
      return dn.length >= 4 && trimmed.includes(dn)
    })
    if (substringIdx >= 0) return substringIdx

    // 3. The label appears inside a document's drawing_number
    const reverseIdx = multiPageDocs.findIndex(d => {
      const dn = (d.properties.drawing_number ?? '').trim().toLowerCase()
      return trimmed.length >= 4 && dn.length > 0 && dn.includes(trimmed)
    })
    if (reverseIdx >= 0) return reverseIdx

    // 4. Label as 1-based sheet number
    const num = parseInt(trimmed, 10)
    if (!Number.isNaN(num) && num >= 1 && num <= multiPageDocs.length) {
      return num - 1
    }

    return -1
  }, [multiPageDocs, multiPageSetId])

  // Resolve drawing_to_drawing_connector to target sheet and navigate.
  // Prefers backend-linked connected_to_graph_id for exact navigation;
  // falls back to fuzzy label matching within the multi-page set.
  const handleDrawingConnectorClick = useCallback((label: string, extraProperties?: Record<string, string>) => {
    const targetGraphId = extraProperties?.connected_to_graph_id
    const targetNodeId = extraProperties?.connected_to_node_id
    const targetDocId = extraProperties?.connected_to_document_id

    // Direct navigation via backend linking data
    if (targetGraphId) {
      // Use current URL docIds as fallback if multiPageDocs hasn't loaded yet
      const newDocIds = multiPageDocs.length > 0 ? multiPageDocs.map(d => d.id).join(',') : (docIdsParam || '')
      navigate(
        buildConnectorNavUrl(
          { graphId: targetGraphId, nodeId: targetNodeId, documentId: targetDocId },
          {
            multiPageSetId,
            docIds: newDocIds || null,
            pnidPages: pnidPagesParam,
            pnidPageGraphIds: pnidPageEntries?.map(e => e.graphId) ?? null,
          }
        )
      )
      return
    }

    // Fallback: fuzzy label matching within multi-page set
    const trimmed = label.trim()
    if (!trimmed) return

    if (!multiPageSetId || multiPageDocs.length === 0) {
      toast.info(t('data_catalog:graph_editor.drawing_connector_open_multi_page', 'Open this diagram as part of a multi-page set to navigate to referenced drawings.'))
      return
    }

    const idx = resolveConnectorLabel(trimmed)
    if (idx >= 0) {
      navigateToSheet(idx)
      return
    }

    toast.info(t('data_catalog:graph_editor.drawing_connector_not_found', { label: trimmed }))
  }, [multiPageDocs, multiPageSetId, docIdsParam, pnidPagesParam, pnidPageEntries, navigate, navigateToSheet, resolveConnectorLabel, t])

  // Sheet connector callbacks
  const handleAddConnector = useCallback((edge: SheetConnectorEdge) => {
    setConnectorModalEdge(edge)
    setEditingConnector(null)
    setConnectorModalOpen(true)
  }, [])

  const handleConnectorClick = useCallback((connector: SheetConnector) => {
    if (connector.target_document_id && multiPageDocs.length > 0) {
      const idx = multiPageDocs.findIndex(d => d.id === connector.target_document_id)
      if (idx >= 0) navigateToSheet(idx)
    }
  }, [multiPageDocs, navigateToSheet])

  const handleConnectorContextMenu = useCallback((e: React.MouseEvent, connector: SheetConnector) => {
    e.preventDefault()
    setContextMenuConnector({ connector, x: e.clientX, y: e.clientY })
  }, [])

  // Auto-switch to Graph Preview mode when no bounding boxes exist
  useEffect(() => {
    if (!hasBoundingBoxes && activeTab === EDITOR_MODE) {
      handleTabChange(GRAPH_PREVIEW)
    }
  }, [hasBoundingBoxes, activeTab, handleTabChange])

  // Wrap publishGraph to return a boolean for BoardLayout's onSave interface.
  // QA/ENG-2595: save neither blocks nor warns on missing Type — completeness is hard-enforced
  // per-object by the (disabled) Approve button in the Node Details / Add Object panels, which
  // supersedes any save-time signal. Save just persists.
  const handleSaveGraph = useCallback(async (): Promise<boolean> => {
    setIsSaving(true)
    try {
      await publishGraph()
      return true
    } catch (error) {
      console.error('Failed to save graph:', error)
      toast.error(t('data_catalog:graph_editor.errors.save_failed'))
      return false
    } finally {
      setIsSaving(false)
    }
  }, [publishGraph, t])

  const currentDoc = multiPageDocs[currentSheetIndex]
  const currentDrawingNumber = currentDoc?.properties?.drawing_number ?? ''

  const startEditingDrawingNumber = useCallback(() => {
    setDrawingNumberEdit(currentDrawingNumber)
    setEditingDrawingNumber(true)
  }, [currentDrawingNumber])

  const cancelEditingDrawingNumber = useCallback(() => {
    setEditingDrawingNumber(false)
    setDrawingNumberEdit('')
  }, [])

  const saveDrawingNumber = useCallback(async () => {
    if (!currentDoc?.id) return
    const value = drawingNumberEdit.trim()
    setSavingDrawingNumber(true)
    try {
      await apiService.updateDocumentMetadata(currentDoc.id, { drawing_number: value })
      setMultiPageDocs(prev =>
        prev.map((d, i) =>
          i === currentSheetIndex
            ? { ...d, properties: { ...d.properties, drawing_number: value || undefined } }
            : d
        )
      )
      toast.success(t('data_catalog:graph_editor.drawing_number_saved'))
      setEditingDrawingNumber(false)
      setDrawingNumberEdit('')
    } catch {
      toast.error(t('data_catalog:graph_editor.drawing_number_save_error'))
    } finally {
      setSavingDrawingNumber(false)
    }
  }, [currentDoc?.id, currentSheetIndex, drawingNumberEdit, t])

  if (!graph_id) {
    // TODO: Make a more user friendly component for this case
    return <div>{t('data_catalog:graph_editor.errors.graph_id_missing')}</div>
  }

  // Sheet navigation for multi-sheet drawings — a grouped multi-file set, or a
  // single multi-page PDF's pages. Rendered from the shared page bar so the V2
  // chrome can show the same control without duplicating the markup.
  const hasMultiPageNav = (multiPageSetId || pnidPageEntries) && multiPageDocs.length > 0
  // Grouped multi-file set features (drawing number, edge connectors) — NOT for single-PDF pages
  const isGroupedMultiPageSet = !!multiPageSetId && multiPageDocs.length > 0
  const sheetPageBar = hasMultiPageNav ? (
    <SheetPageBar
      variant="v1"
      currentIndex={currentSheetIndex}
      total={multiPageDocs.length}
      sheetNames={sheetNames}
      onNavigate={navigateToSheet}
      disabled={loadingMultiPage}
    />
  ) : undefined
  // The legacy header renders this slot, so an SLD's view switch rides in it
  // there; the V2 navbar carries its own.
  const sheetNavigationElement =
    !isV2 && sldViews ? (
      <>
        <SldViewSwitch views={sldViews} onSelect={(view) => void openSldView(view)} />
        {isParsedSldView && (
          <span className="text-xs text-amber-800 dark:text-amber-200">
            {t('data_catalog:graph_editor.sld_view.read_only', { defaultValue: 'Read-only' })}
          </span>
        )}
        {sheetPageBar}
      </>
    ) : (
      sheetPageBar
    )

  const drawingNumberEditor = isGroupedMultiPageSet ? (
    multiPageDocs.length > 0 ? (
      <DrawingNumberEditor
        currentDrawingNumber={currentDrawingNumber}
        editingDrawingNumber={editingDrawingNumber}
        drawingNumberEdit={drawingNumberEdit}
        savingDrawingNumber={savingDrawingNumber}
        onEditChange={setDrawingNumberEdit}
        onSave={saveDrawingNumber}
        onCancel={cancelEditingDrawingNumber}
        onStartEdit={startEditingDrawingNumber}
        t={t}
      />
    ) : (
      <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-card border rounded-lg shadow-sm invisible">
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {t('data_catalog:graph_editor.drawing_number_label')}:
        </span>
        <span className="text-sm text-gray-700 dark:text-gray-300 min-w-0 truncate">—</span>
        <Button variant="ghost" size="sm" className="h-8 shrink-0" tabIndex={-1}>
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    )
  ) : null

  const boardActions = [
    {
      onClick: openBugReport,
      element: (
        <Bug className="w-6 h-6 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer" />
      ),
    },
    {
      // FS-623: labeling filter (by Category/Subcategory/Type/Tag/Annotation/Note).
      onClick: () => switchRightPanel(activeRightPanel === 'labelFilter' ? null : 'labelFilter'),
      element: (
        <Button variant="outline" className={`gap-1.5 ${showFilterPanel ? 'border-primary text-primary bg-primary/5' : ''}`}>
          <Filter className="w-4 h-4" />
          {t('data_catalog:graph_editor.filter', { defaultValue: 'Filter' })}
        </Button>
      ),
    },
    {
      onClick: () => {},
      element: <VersionSelector graphId={graph_id} />,
    },
    {
      onClick: () => handleSaveGraph(),
      element: (
        <Button disabled={!currentState.isModified}>
          {t('data_catalog:graph_editor.controls.save_changes')}
        </Button>
      ),
    },
    ...(drawingNumberEditor ? [{
      onClick: () => {},
      element: drawingNumberEditor,
    }] : []),
  ]

  // Derive document name for V2 breadcrumb. Falls back to '' (not the page
  // title) while the name is still resolving — the navbar renders a skeleton
  // for the empty case so the second crumb never duplicates the first
  // ("Data Catalog > Data Catalog" flicker).
  //
  // A single multi-page PDF's sheets are SYNTHETIC docs named "Page N"
  // (see the pnidPageEntries effect), so using the sheet name here made the
  // crumb read "Data > Page 1" instead of the file. Only a grouped set's
  // sheets are real documents with real names. The sheet number is carried by
  // the page bar, not the crumb.
  const isSyntheticPageDoc = !!pnidPageEntries && !multiPageSetId
  const documentDisplayName =
    (isSyntheticPageDoc ? undefined : multiPageDocs[currentSheetIndex]?.name) ||
    resolvedDocName ||
    docNameParam ||
    ''

  // Shared editor + modals
  const editorContent = (
    <>
      <EditorWrapper
        graphId={graph_id}
        // An SLD page's parse: shown read-only, never edited (VISN-304).
        isReadOnly={isParsedSldView}
        documentId={documentId || undefined}
        activeTab={activeTab}
        jsonMode={jsonMode}
        onBoundingBoxStatusChange={setHasBoundingBoxes}
        onDisciplineResolved={onDisciplineResolved}
        onDrawingConnectorClick={handleDrawingConnectorClick}
        onNavigateToSheet={hasMultiPageNav ? navigateToSheet : undefined}
        currentSheetIndex={currentSheetIndex}
        totalSheets={multiPageDocs.length}
        sheetNames={sheetNames}
        sheetNavDisabled={loadingMultiPage}
        sheetNavigation={sheetNavigationElement}
        sheetConnectors={sheetConnectors}
        multiPageDocs={multiPageDocs.map(d => ({ id: d.id, name: d.name }))}
        onAddConnector={isGroupedMultiPageSet ? handleAddConnector : undefined}
        onConnectorClick={handleConnectorClick}
        onConnectorContextMenu={handleConnectorContextMenu}
        pnidPageNumber={pnidPageEntries?.[currentSheetIndex]?.page}
        enterCreateMode={addObjectMode}
        onCreateModeExit={() => setActiveRightPanel(null)}
        clearNodeSelection={showReviewPanel || showHistoryPanel || showConnectedPanel || showFilterPanel || showTablesPanel}
        externalPanelOpen={showReviewPanel || showHistoryPanel || showConnectedPanel || showFilterPanel || showTablesPanel}
        reviewHighlightNodeIds={showReviewPanel ? reviewHighlightIds : showFilterPanel ? filterHighlightIds : undefined}
        dimUnmatchedHighlights={showFilterPanel && filterFocusMode}
        // Cells are drawn only while the grid panel is open, and only for the table on
        // screen — a table is hundreds of boxes.
        visibleTableIndex={showTablesPanel ? activeTableIndex : null}
        focusedTableCellId={showTablesPanel ? focusedTableCellId : null}
        onTableCellClick={
          showTablesPanel
            ? (id) => {
                setCanvasPickedCellId(id)
                // A pick-off (null) also drops the grid's focused cell: the canvas draws a
                // cell highlighted while it is EITHER the picked one or the focused one, so
                // clearing only half of that would leave the box looking selected.
                if (id === null) setFocusedTableCellId(null)
              }
            : undefined
        }
        onSelectedCellIdsChange={setSelectedCellIds}
        drawTableRegion={drawingTableRegion}
        onTableRegionDrawn={(region) => {
          setDrawingTableRegion(false)
          setPendingTableRegion(region)
        }}
        onNodeSelected={() => setActiveRightPanel(null)}
        onDocumentNameResolved={setResolvedDocName}
        externalSearchTerm={isV2 ? v2SearchTerm : undefined}
        onSearchMatchInfo={isV2 ? setV2SearchMatchInfo : undefined}
        onSearchNext={isV2 ? v2SearchNextRef : undefined}
        onSearchPrevious={isV2 ? v2SearchPreviousRef : undefined}
      />
      {documentId && (
        <SheetConnectorModal
          isOpen={connectorModalOpen}
          onClose={() => { setConnectorModalOpen(false); setEditingConnector(null) }}
          documentId={documentId}
          defaultEdge={connectorModalEdge}
          editingConnector={editingConnector}
          multiPageDocs={multiPageDocs.map(d => ({ id: d.id, name: d.name }))}
          existingConnectorIds={sheetConnectors.map(c => c.connector_id)}
          onCreate={async (dto) => {
            if (dto.direction === 'out' && dto.target_document_id) {
              const result = await createSheetConnectorWithMirror(dto)
              if (result.mirror) {
                toast.success(t('data_catalog:graph_editor.toasts.mirror_connector_created'))
              }
            } else {
              await createSheetConnector(dto)
            }
          }}
          onUpdate={updateSheetConnector}
        />
      )}
      {contextMenuConnector && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenuConnector(null)} />
          <div
            className="fixed z-50 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-md shadow-lg py-1 min-w-[120px]"
            style={{ left: contextMenuConnector.x, top: contextMenuConnector.y }}
          >
            <button
              className="w-full px-3 py-1.5 text-sm text-left hover:bg-gray-100 dark:hover:bg-dark-accent"
              onClick={() => {
                setEditingConnector(contextMenuConnector.connector)
                setConnectorModalEdge(contextMenuConnector.connector.edge)
                setConnectorModalOpen(true)
                setContextMenuConnector(null)
              }}
            >
              {t('data_catalog:graph_editor.context_menu.edit')}
            </button>
            <button
              className="w-full px-3 py-1.5 text-sm text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={async () => {
                try {
                  await removeSheetConnector(contextMenuConnector.connector.id)
                  toast.success(t('data_catalog:graph_editor.toasts.connector_deleted'))
                } catch {
                  toast.error(t('data_catalog:graph_editor.toasts.connector_delete_failed'))
                }
                setContextMenuConnector(null)
              }}
            >
              {t('data_catalog:graph_editor.context_menu.delete')}
            </button>
          </div>
        </>
      )}
    </>
  )

  // ── V2 layout: AppSidebar + custom navbars ──
  if (isV2) {
    // Embed mode strips all chrome so just the editor surface renders —
    // used by the KG floating preview iframe.
    if (isEmbedded) {
      return (
        <div className="flex h-screen w-screen overflow-hidden">{editorContent}</div>
      )
    }
    return (
      <div className="flex h-screen">
        {/* Guard every exit path (breadcrumb, sidebar, browser Back, tab close)
            so unsaved annotations aren't silently dropped. */}
        <UnsavedChangesGuard isModified={!isParsedSldView && currentState.isModified} onSave={handleSaveGraph} />
        {/* FS-623 follow-up: while a save is in flight, a transparent full-screen
            overlay turns the cursor to a wait/progress state and blocks interaction,
            so a slow persist reads as "working" rather than an unresponsive editor. */}
        {isSaving && <div className="fixed inset-0 z-[100] cursor-progress" aria-hidden="true" />}
        <AppSidebar />
        <div className="flex flex-1 flex-col min-w-0">
          <GraphEditorNavbarV2
            documentName={documentDisplayName}
            isSaved={!currentState.isModified}
            isSaving={isSaving}
            isModified={!isParsedSldView && currentState.isModified}
            onSave={handleSaveGraph}
            onHistoryClick={() => switchRightPanel(activeRightPanel === 'history' ? null : 'history')}
            isHistoryActive={showHistoryPanel}
            fromBoardName={boardName}
            sldViews={sldViews}
            onSldViewSelect={(view) => void openSldView(view)}
          />
          <GraphEditorSubNavbarV2
            activeTab={activeTab}
            hasBoundingBoxes={hasBoundingBoxes}
            onTabChange={handleTabChange}
            pendingReviewCount={pendingReviewCount}
            onSearchTermChange={setV2SearchTerm}
            searchMatchInfo={v2SearchMatchInfo}
            onSearchNext={() => v2SearchNextRef.current?.()}
            onSearchPrevious={() => v2SearchPreviousRef.current?.()}
            onDownload={isAnnotator ? undefined : () => {
              if (activeTab === JSON_VIEW) {
                window.dispatchEvent(new Event('json-download'))
              } else {
                window.dispatchEvent(new Event('graph-download'))
              }
            }}
            onCopy={activeTab === JSON_VIEW ? () => {
              window.dispatchEvent(new Event('json-copy'))
            } : undefined}
            onPendingReviewClick={() => switchRightPanel(activeRightPanel === 'review' ? null : 'review')}
            isPendingReviewActive={showReviewPanel}
            // An SLD page's parse is read-only: nothing to review, filter, tabulate or add.
            readOnly={isParsedSldView}
            onFilterClick={
              isParsedSldView ? undefined : () => switchRightPanel(activeRightPanel === 'labelFilter' ? null : 'labelFilter')
            }
            isFilterActive={showFilterPanel}
            onTablesClick={
              isParsedSldView
                ? undefined
                : () => {
                    // Closing must abandon an in-progress region draw, else the canvas stays in
                    // draw mode with nothing listening. Decided outside the state updater, which
                    // has to stay pure.
                    if (showTablesPanel) {
                      setDrawingTableRegion(false)
                      setPendingTableRegion(null)
                    }
                    setActiveRightPanel(showTablesPanel ? null : 'tables')
                  }
            }
            isTablesActive={showTablesPanel}
            onAddObject={() => switchRightPanel(activeRightPanel === 'addObject' ? null : 'addObject')}
            onConnectedClick={() => switchRightPanel(activeRightPanel === 'connected' ? null : 'connected')}
            isConnectedActive={showConnectedPanel}
            showConnected={showConnected && !isParsedSldView}
            isInternalOrg={!!user?.isInternalOrg}
            isAnnotator={isAnnotator}
            jsonMode={jsonMode}
            onJsonModeChange={setJsonMode}
          />
          {isParsedSldView && (
            <div
              role="status"
              className="shrink-0 border-b border-[#e5e5e5] dark:border-dark-border bg-amber-50 dark:bg-dark-accent px-6 py-2 text-sm text-amber-900 dark:text-amber-200"
            >
              {t('data_catalog:graph_editor.sld_view.parsed_read_only', {
                defaultValue:
                  "Parsed view: the parse's suggestions for this page. It is read-only and replaced on every re-parse; annotate in the Annotation view.",
              })}
            </div>
          )}
          <div className="flex-1 min-h-0 flex overflow-hidden">
            {editorContent}
            <PendingReviewPanelV2
              isOpen={showReviewPanel}
              onClose={() => setActiveRightPanel(null)}
              onHighlightChange={setReviewHighlightIds}
            />
            <LabelingFilterPanelV2
              isOpen={showFilterPanel}
              onClose={() => setActiveRightPanel(null)}
              onHighlightChange={setFilterHighlightIds}
              onFocusModeChange={setFilterFocusMode}
            />
            <TableGridPanelV2
              isOpen={showTablesPanel}
              onClose={() => {
                setActiveRightPanel(null)
                setDrawingTableRegion(false)
                setPendingTableRegion(null)
                setFocusedTableCellId(null)
              }}
              // Clicking again while armed disarms, so they can back out without drawing a
              // throwaway box.
              onCreateTable={() => {
                setPendingTableRegion(null)
                setDrawingTableRegion((armed) => !armed)
              }}
              isDrawingRegion={drawingTableRegion}
              pendingRegion={pendingTableRegion}
              onPendingRegionConsumed={() => setPendingTableRegion(null)}
              onActiveTableChange={setActiveTableIndex}
              onFocusCellChange={(id) => {
                setFocusedTableCellId(id)
                // Focusing settles the canvas pick. Without clearing it, clicking the SAME
                // cell again wouldn't change the prop, so the grid would never re-focus.
                setCanvasPickedCellId(null)
              }}
              focusCellId={canvasPickedCellId}
              selectedCellIds={selectedCellIds}
            />
            <VersionHistoryPanelV2
              isOpen={showHistoryPanel}
              graphId={graph_id}
              onSave={handleSaveGraph}
            />
            {graph_id && (
              <ConnectedPnidsPanelV2
                isOpen={showConnectedPanel}
                onClose={() => setActiveRightPanel(null)}
                graphId={graph_id}
                onOpenTarget={handleDrawingConnectorClick}
                onManualConnectorClick={handleConnectorClick}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Legacy layout: BoardLayout header ──
  return (
    <BoardLayout
      name={boardName || t('nav:menu.data', 'Data')}
      backUrl={''}
      handleBackCallback={navigateBack}
      actions={boardActions}
      tabs={() => (
        <EditorTabs
          tabKeys={tabKeys}
          activeTab={activeTab}
          hasBoundingBoxes={hasBoundingBoxes}
          onTabChange={handleTabChange}
          getTabLabel={getTabLabel}
          noBboxText={t('data_catalog:graph_editor.tabs.no_bbox')}
        />
      )}
      hasUnsavedChanges={currentState.isModified}
      onSave={handleSaveGraph}
    >
      {/* FS-623: labeling filter panel as a flex sibling of the canvas (V1 chrome).
          Mirrors the V2 layout container so editorContent sizes identically. */}
      <div className="flex-1 min-w-0 flex overflow-hidden">
        {editorContent}
        <LabelingFilterPanelV2
          isOpen={showFilterPanel}
          onClose={() => setActiveRightPanel(null)}
          onHighlightChange={setFilterHighlightIds}
        />
      </div>
    </BoardLayout>
  )
}

/**
 * The route's single taxonomy boundary.
 *
 * `EditorWrapper` resolves the discipline from the graph document it already
 * holds and reports it up, so this costs no extra round trip — the route
 * deliberately makes none when the URL already carries its page map.
 */
export default function GraphEditor() {
  const [discipline, setDiscipline] = useState<DrawingDiscipline>('pnid')
  return (
    <TaxonomyProvider discipline={discipline}>
      <GraphEditorRoute onDisciplineResolved={setDiscipline} />
    </TaxonomyProvider>
  )
}
